/**
 * SyncStatusIndicator — global topbar sync pill, icon-only.
 *
 * Reads the single OfflineProvider phase and conveys the sync state purely
 * through an icon + colour (label lives in the tooltip / aria-label):
 *   spinner (teal)    → syncing            green check   → sync completed
 *   red triangle      → sync failed (retry) download+pulse → caching offline
 *   cloud + badge     → changes pending     grey check     → synced/up-to-date
 *   cloud-off (amber) → offline
 * Clicking triggers a manual sync.
 */
import type { ReactNode } from "react";
import { Cloud, CloudOff, RefreshCw, CheckCircle2, AlertTriangle, DownloadCloud } from "lucide-react";
import { cn, relativeTime } from "@/lib/utils";
import { useOffline } from "@/offline/OfflineProvider";

export function SyncStatusIndicator({ className }: { className?: string }) {
  const { online, pendingCount, phase, warming, lastSyncedAt, lastError, runSyncNow } = useOffline();
  const has = pendingCount > 0;

  let icon: ReactNode;
  let tone: string;
  let title: string;
  let showBadge = false;

  if (phase === "syncing") {
    icon = <RefreshCw className="size-4 animate-spin" />;
    tone = "text-primary";
    title = "Syncing with the server…";
  } else if (phase === "success") {
    icon = <CheckCircle2 className="size-4" />;
    tone = "text-emerald-600 dark:text-emerald-500";
    title = "Sync completed successfully.";
  } else if (phase === "error") {
    icon = <AlertTriangle className="size-4" />;
    tone = "text-rose-600 dark:text-rose-500 hover:bg-surface-hover";
    title = `${lastError ?? "Sync failed."} Tap to retry.`;
  } else if (warming) {
    // Sync is done and the app is usable — quiet background offline caching.
    icon = <DownloadCloud className="size-4 animate-pulse" />;
    tone = "text-fg-muted hover:bg-surface-hover";
    title = "Caching your inspections for offline use — the app is ready; you can keep working.";
  } else if (online && has) {
    icon = <Cloud className="size-4" />;
    tone = "text-fg-muted hover:bg-surface-hover";
    title = `${pendingCount} change${pendingCount === 1 ? "" : "s"} waiting to sync — tap to sync now`;
    showBadge = true;
  } else if (online) {
    // Steady "everything is synced" status (persistent, muted check).
    icon = <CheckCircle2 className="size-4" />;
    tone = "text-fg-muted hover:bg-surface-hover";
    title = lastSyncedAt ? `All changes synced · Last synced ${relativeTime(lastSyncedAt)}` : "All changes synced";
  } else {
    icon = <CloudOff className="size-4" />;
    tone = "text-amber-600 dark:text-amber-500";
    title = has
      ? `Offline — ${pendingCount} change${pendingCount === 1 ? "" : "s"} saved locally`
      : "Offline — changes will save locally";
    showBadge = has;
  }

  return (
    <button
      type="button"
      onClick={() => online && runSyncNow()}
      data-tour="sync-status"
      title={title}
      aria-label={title}
      aria-live="polite"
      className={cn(
        "relative inline-flex size-9 select-none items-center justify-center rounded-md transition-colors",
        tone,
        className,
      )}
    >
      {icon}
      {showBadge ? (
        <span
          className={cn(
            "absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-4",
            online ? "bg-primary text-primary-fg" : "bg-amber-500 text-white",
          )}
        >
          {pendingCount}
        </span>
      ) : null}
    </button>
  );
}
